/**
 * Order-form item helpers (Online store Phase 1, slice E).
 *
 * A product reaches the order form in one of two shapes:
 *   - Shopify-synced: `variants[]` is Shopify's JSON (ids are GIDs) and the
 *     order's line carries `shopify_variant_id`, which order creation sends
 *     to Shopify. Unchanged behaviour.
 *   - Native (built in Online store → Products): `catalog_variants[]` rows
 *     with UUID ids. The line carries `variant_id` and never a
 *     `shopify_variant_id`, so nothing native ever reaches Shopify.
 *
 * Both are normalised to one option shape for the size-tier picker:
 *   { id, title, price, sku, inventoryQuantity, inventoryTracked, native }
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isNativeVariantId = (id) => UUID_RE.test(String(id || ''));

const nativeOption = (v) => ({
  id: v.id,
  title: v.title,
  price: v.price,
  sku: v.sku || null,
  inventoryQuantity: v.inventory_quantity,
  inventoryTracked: !!v.track_inventory,
  native: true,
});

/**
 * The options the picker should offer for a product: Shopify's variants for a
 * synced product, the catalog rows for a native one with more than one
 * active option, else none.
 */
export function productOptions(product) {
  if (!product) return [];
  if (product.is_shopify_synced) return Array.isArray(product.variants) ? product.variants : [];
  const rows = (product.catalog_variants || []).filter((v) => v.status !== 'archived');
  return rows.length > 1 ? rows.map(nativeOption) : [];
}

/** The native default variant row of a product, if it has one. */
export function defaultNativeVariant(product) {
  const rows = (product && product.catalog_variants) || [];
  return rows.find((v) => v.is_default) || rows[0] || null;
}

/**
 * Fields for an order line created from `product`, merged over `base`.
 * Mirrors what the order form stored before, plus `variant_id` for native
 * products.
 */
export function itemFromProduct(base, product) {
  const options = productOptions(product);
  const multiple = options.length > 1;
  const single = options.length === 1;

  let shopifyVariantId = null;
  let variantId = null;
  if (product.is_shopify_synced) {
    shopifyVariantId = single ? options[0].id : multiple ? null : product.shopify_variant_id;
  } else if (!multiple) {
    const def = defaultNativeVariant(product);
    variantId = def ? def.id : null;
  }

  return {
    ...base,
    name: product.title,
    selectedProductTitle: product.title,
    price: parseFloat(product.price || 0).toFixed(2),
    originalPrice: parseFloat(product.price || 0).toFixed(2),
    priceNote: '',
    image_url: product.image_url,
    productId: product.id,
    shopify_product_id: product.shopify_id,
    shopify_variant_id: shopifyVariantId,
    variant_id: variantId,
    isProduct: true,
    isShopifyProduct: !!product.is_shopify_synced,
    inventoryQuantity: product.inventory_quantity,
    inventoryTracked: product.track_inventory,
    variants: options,
    selectedVariant: null,
    needsVariantSelection: multiple,
    tags: product.tags || [],
    // Phase 2: the date picker needs the slowest item's notice
    leadTimeDays: Number(product.lead_time_days) || 0,
  };
}

/** Apply a chosen option to a line: name suffix, price, and the right id. */
export function applyVariant(item, variant) {
  const baseName = (item.name || '').split(' - ')[0];
  const native = variant.native === true || (isNativeVariantId(variant.id) && !String(variant.id).startsWith('gid://'));
  return {
    ...item,
    name: `${baseName} - ${variant.title}`,
    price: parseFloat(variant.price || 0).toFixed(2),
    originalPrice: parseFloat(variant.price || 0).toFixed(2),
    priceNote: '',
    shopify_variant_id: native ? null : variant.id,
    variant_id: native ? variant.id : null,
    selectedVariant: variant,
    needsVariantSelection: false,
    sku: variant.sku,
    inventoryQuantity: variant.inventoryQuantity,
  };
}

/**
 * Returns the order's non-complimentary items, or the full list if every
 * item is complimentary (defensive fallback — shouldn't happen in practice).
 *
 * Used wherever the UI picks a single "representative" item for preview
 * purposes — the Mother's Day 2026 Poppy Seed Packet auto-add ships with
 * a Shopify image, which would otherwise win `items.find(i => i.image_url)`
 * against custom staff-entered arrangements that have no image_url.
 */
export function nonComplimentaryItems(items) {
  const all = items || [];
  const real = all.filter(i => !(i.is_complimentary === true || i.isComplimentary === true));
  return real.length > 0 ? real : all;
}
